import { useState, useEffect } from "react";
import { MathJaxContext } from "better-react-mathjax";
import "./question-bank.css";
import { Check, Hash, ChevronRight } from "lucide-react";
import { PHASES } from "./types";
import { MATHJAX_CONFIG } from "./mathjax-macros";

import { ExtractionEngine } from "./ExtractionEngine";
import { GenerationEngine } from "./GenerationEngine";
import { EvaluatorSystem } from "./EvaluatorSystem";
import { ReviewUI } from "./ReviewUI";
import { CoverageTracker } from "./CoverageTracker";
import { QBErrorBoundary } from "./QBErrorBoundary";

const phaseComponents: Record<string, React.FC> = {
  extraction: ExtractionEngine,
  generation: GenerationEngine,
  evaluator: EvaluatorSystem,
  review: ReviewUI,
  coverage: CoverageTracker,
};

// Survives the unmount caused by Dashboard's key={activeTab}, so returning to
// the tab keeps you on the phase you were looking at. Resets on page reload.
let _cachedPhase = 0;

export function QuestionBank() {
  const [activePhase, setActivePhase] = useState(_cachedPhase);
  const phase = PHASES[activePhase] ?? PHASES[0];
  const ActiveComponent = phase ? phaseComponents[phase.id] : undefined;

  useEffect(() => {
    _cachedPhase = activePhase;
  }, [activePhase]);

  return (
    <MathJaxContext config={MATHJAX_CONFIG}>
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border bg-card shadow-sm">
      <div className="sticky top-0 z-10 border-b bg-muted/30 px-6 py-2.5">
        <div className="flex items-center gap-1.5">
          {PHASES.map((phase, i) => (
            <div key={phase.id} className="flex items-center gap-1.5">
              <button
                onClick={() => setActivePhase(i)}
                className={`flex cursor-pointer items-center gap-1 rounded-md border-none px-2 py-1 text-[11px] font-semibold transition-all ${
                  i === activePhase
                    ? "bg-primary text-primary-foreground"
                    : i < activePhase
                      ? "bg-transparent text-foreground"
                      : "bg-transparent text-muted-foreground"
                }`}
              >
                {i < activePhase ? (
                  <Check className="h-2.5 w-2.5" />
                ) : (
                  <Hash className="h-2.5 w-2.5" />
                )}
                {phase.label}
              </button>
              {i < PHASES.length - 1 && (
                <ChevronRight className="h-3 w-3 text-muted-foreground/40" />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4">
        {/* key resets the boundary on phase change, so one bad phase never traps the user */}
        <QBErrorBoundary key={activePhase} label={phase?.label}>
          {ActiveComponent ? <ActiveComponent /> : null}
        </QBErrorBoundary>
      </div>
    </div>
    </MathJaxContext>
  );
}
